import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play, Expand } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { SectionHead } from "./Reveal";

export default function JpgSlider({ images = [] }) {
  const [idx, setIdx] = useState(0);
  const [auto, setAuto] = useState(true);
  const [zoom, setZoom] = useState(-1);
  const touchX = useRef(null);
  const count = images.length;

  useEffect(() => {
    if (!auto || count < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 4200);
    return () => clearInterval(t);
  }, [auto, count]);

  if (count === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + count) % count);
  const next = () => setIdx((i) => (i + 1) % count);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 48) (dx > 0 ? prev : next)();
    touchX.current = null;
  };

  return (
    <section id="previews" className="py-24 bg-white border-y border-slate-100">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead
          index="03" label="Inside the Pages"
          title="High-res JPG previews — see exactly what you're buying"
          sub="Sample pages, topper handwriting, planners and mindmaps. Swipe, tap or go full-screen."
        />

        <div className="relative mt-12" data-testid="jpg-slider-container">
          <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/60 aspect-[16/9] sm:aspect-[21/10] bg-slate-100" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className="flex h-full w-full transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]" style={{ transform: `translateX(-${idx * 100}%)` }}>
              {images.map((img, i) => (
                <div key={img.id} className="relative h-full w-full shrink-0">
                  <img src={img.image_url} alt={img.title || `Slide ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                    <p className="font-display font-bold text-white text-sm sm:text-lg max-w-lg">{img.title || `Preview ${i + 1}`}</p>
                    <span className="font-mono text-xs text-amber-400 whitespace-nowrap">{String(i + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}</span>
                  </div>
                  <button
                    onClick={() => setZoom(i)}
                    className="absolute top-4 right-4 rounded-full border border-white/20 bg-ink/60 p-2.5 text-white backdrop-blur hover:bg-amber-500 hover:text-ink transition-colors"
                    data-testid={`jpg-zoom-open-btn-${i}`}
                    aria-label="Full screen"
                  >
                    <Expand size={16} />
                  </button>
                </div>
              ))}
            </div>

            <button onClick={prev} data-testid="jpg-slider-prev-btn" aria-label="Previous"
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-ink/55 p-3 text-white backdrop-blur-xl hover:bg-amber-500 hover:text-ink transition-colors">
              <ChevronLeft size={20} />
            </button>
            <button onClick={next} data-testid="jpg-slider-next-btn" aria-label="Next"
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-ink/55 p-3 text-white backdrop-blur-xl hover:bg-amber-500 hover:text-ink transition-colors">
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <div className="flex gap-2">
              {images.map((img, i) => (
                <button key={img.id} onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`}
                  data-testid={`jpg-slider-dot-${i}`}
                  className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-amber-500" : "w-2 bg-slate-300 hover:bg-slate-400"}`} />
              ))}
            </div>
            <button onClick={() => setAuto(!auto)} data-testid="jpg-autoplay-toggle"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 hover:border-amber-500 hover:text-amber-600">
              {auto ? <Pause size={13} /> : <Play size={13} />} {auto ? "Pause" : "Play"}
            </button>
          </div>
        </div>
      </div>

      <Dialog open={zoom >= 0} onOpenChange={(o) => !o && setZoom(-1)}>
        <DialogContent className="max-w-5xl border-0 bg-ink/95 p-2" data-testid="jpg-zoom-modal">
          {zoom >= 0 && images[zoom] && (
            <img src={images[zoom].image_url} alt={images[zoom].title} className="max-h-[82vh] w-full rounded-2xl object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
